import { CreditCard, AlertTriangle, Loader2, Smartphone, RefreshCw, Clock, XCircle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PointTech } from "@/components/terminal/PointTech";

// Cobro con terminal Mercado Pago Point — presentacional. Toda la lógica vive en Terminal.jsx.
export const PointDialog = ({
  pointOpen, cerrarPagoPoint, coTicket, subtotalSalida, pointStatus,
  propinasActivas, topePropinaMonto, propina, setPropina, propinaCustom, setPropinaCustom,
  propinaExcede, topePropinaPct, propinaNum, trasladaComision, cargoServicio, granTotalTarjeta,
  pointDevicesLoading, pointDevices, cargarDevices, pointDevice, elegirDevice, enviarCobroPoint,
  pointMsg, pointOrderStatus, pointTech, consultarManual, cancelarPendiente,
  setPointStatus, setPointTxId, setPointTech, setPointOrderStatus, setPointMsg,
}) => (
  <Dialog open={pointOpen} onOpenChange={(o) => { if (!o) cerrarPagoPoint(); }}>
    <DialogContent className="rounded-none border-[#009EE3]/40 bg-[#0A0A0A] text-white sm:max-w-md max-h-[90dvh] overflow-y-auto overflow-x-hidden" data-testid="point-dialog">
      <DialogHeader>
        <DialogTitle className="font-display font-black text-white flex items-center gap-2"><CreditCard className="h-5 w-5 text-[#38BDF8]" /> Cobro con tarjeta · Mercado Pago</DialogTitle>
        <DialogDescription className="text-white/50">
          {coTicket ? `Folio ${coTicket.folio_impreso} · $${subtotalSalida.toFixed(2)}` : ""}
        </DialogDescription>
      </DialogHeader>

      {pointStatus === "idle" && (
        <div className="space-y-4 pt-1" data-testid="point-idle">
          {propinasActivas && subtotalSalida > 0 && (
            <div className="border border-white/10 bg-white/5 p-3" data-testid="point-propina-block">
              <label className="text-[10px] uppercase tracking-wider font-bold text-white/50">Propina (opcional · no facturable)</label>
              <div className="grid grid-cols-4 gap-2 mt-2">
                {[10, 20, 50].map((m) => (
                  <button key={m} type="button" disabled={m > topePropinaMonto + 0.0001} onClick={() => { setPropina(m); setPropinaCustom(""); }} data-testid={`point-propina-${m}`}
                    className={`h-10 border text-xs font-bold uppercase tracking-wider transition-colors disabled:opacity-30 disabled:cursor-not-allowed ${propina === m && !propinaCustom ? "bg-[#FFCC00] text-[#0A0A0A] border-[#FFCC00]" : "border-white/20 text-white/70 hover:border-white/40"}`}>
                    ${m}
                  </button>
                ))}
                <button type="button" onClick={() => { setPropina(0); setPropinaCustom(propinaCustom || "0"); }} data-testid="point-propina-custom-btn"
                  className={`h-10 border text-xs font-bold uppercase tracking-wider transition-colors ${propinaCustom !== "" ? "bg-[#FFCC00] text-[#0A0A0A] border-[#FFCC00]" : "border-white/20 text-white/70 hover:border-white/40"}`}>
                  Otro
                </button>
              </div>
              {propinaCustom !== "" && (
                <input type="number" min="0" step="1" inputMode="decimal" value={propinaCustom} autoFocus
                  onChange={(e) => { const v = e.target.value; setPropinaCustom(v); setPropina(Math.max(0, Number(v) || 0)); }}
                  data-testid="point-propina-custom-input" placeholder="Monto de propina"
                  className={`mt-2 w-full h-10 bg-white/5 border text-white font-mono px-3 outline-none ${propinaExcede ? "border-[#F87171] focus:border-[#F87171]" : "border-white/20 focus:border-[#FFCC00]"}`} />
              )}
              {propinaExcede && (
                <div className="mt-2 flex items-start gap-2 border border-[#F87171]/40 bg-[#F87171]/10 px-3 py-2" data-testid="point-propina-error">
                  <AlertTriangle className="h-4 w-4 text-[#F87171] shrink-0 mt-0.5" />
                  <p className="text-[11px] text-[#F87171] font-bold leading-relaxed">La propina no puede exceder el {Number(topePropinaPct) || 100}% del subtotal (máx. ${topePropinaMonto.toFixed(2)}). Ajusta el monto para continuar.</p>
                </div>
              )}
              {propinaNum > 0 && !propinaExcede && (
                <button type="button" onClick={() => { setPropina(0); setPropinaCustom(""); }} data-testid="point-propina-clear"
                  className="mt-2 text-[11px] font-bold uppercase tracking-wider text-white/40 hover:text-white/70">Quitar propina</button>
              )}
            </div>
          )}
          <div className="border border-white/10 bg-white/5" data-testid="point-desglose">
            <div className="flex justify-between px-3 py-2 text-sm border-b border-white/10">
              <span className="text-white/60">Subtotal</span>
              <span className="font-mono" data-testid="point-subtotal">${subtotalSalida.toFixed(2)}</span>
            </div>
            {propinaNum > 0 && (
              <div className="flex justify-between px-3 py-2 text-sm border-b border-white/10" data-testid="point-propina-row">
                <span className="text-white/60">Propina</span>
                <span className="font-mono text-[#10B981]" data-testid="point-propina">${propinaNum.toFixed(2)}</span>
              </div>
            )}
            {trasladaComision && (
              <div className="flex justify-between px-3 py-2 text-sm border-b border-white/10" data-testid="point-cargo-row">
                <span className="text-white/60">Cargo por servicio</span>
                <span className="font-mono text-[#FFCC00]" data-testid="point-cargo">${cargoServicio.toFixed(2)}</span>
              </div>
            )}
            <div className="flex justify-between px-3 py-2 items-baseline bg-white/5">
              <span className="text-[10px] uppercase tracking-wider font-bold text-white/60">Gran total</span>
              <span className="font-display text-2xl font-black text-[#FFCC00]" data-testid="point-gran-total">${granTotalTarjeta.toFixed(2)}</span>
            </div>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-white/50">Terminal Point</label>
            {pointDevicesLoading ? (
              <div className="mt-1 h-12 flex items-center gap-2 px-3 border border-white/20 bg-white/5 text-white/60 text-sm"><Loader2 className="h-4 w-4 animate-spin" /> Cargando terminales…</div>
            ) : pointDevices.length === 0 ? (
              <div className="mt-1 space-y-2" data-testid="point-no-devices">
                <div className="h-auto py-2 flex items-center gap-2 px-3 border border-[#F87171]/40 bg-[#F87171]/10 text-white/70 text-xs">No se encontraron terminales en esta cuenta.</div>
                <button onClick={cargarDevices} className="text-xs font-bold uppercase tracking-wider text-[#38BDF8] hover:underline">Reintentar</button>
              </div>
            ) : (
              <Select value={pointDevice} onValueChange={elegirDevice}>
                <SelectTrigger data-testid="point-device-select" className="mt-1 h-12 rounded-none bg-white/5 border-white/20 text-white font-mono">
                  <SelectValue placeholder="Selecciona una terminal" />
                </SelectTrigger>
                <SelectContent>
                  {pointDevices.map((d) => (
                    <SelectItem key={d.id} value={d.id} data-testid={`point-device-${d.id}`}>
                      {d.id}{d.operating_mode ? ` · ${d.operating_mode}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
            <p className="text-xs text-white/40 mt-1">Se recuerda durante tu turno en este navegador.</p>
          </div>
          <button onClick={enviarCobroPoint} disabled={!pointDevice || propinaExcede} data-testid="point-enviar-btn"
            className="btn-hard w-full h-14 bg-[#009EE3] text-white border border-[#009EE3] font-bold uppercase tracking-wider flex items-center justify-center gap-2 disabled:opacity-40">
            <Smartphone className="h-5 w-5" /> Enviar a la terminal
          </button>
        </div>
      )}

      {(pointStatus === "sending" || pointStatus === "waiting") && (
        <div className="py-6 flex flex-col items-center text-center gap-5" data-testid="point-waiting">
          <div className="relative">
            <Smartphone className="h-16 w-16 text-[#38BDF8]" strokeWidth={1.5} />
            <Loader2 className="h-6 w-6 animate-spin text-[#FFCC00] absolute -bottom-1 -right-1" />
          </div>
          <div>
            <p className="font-display text-xl font-black tracking-tight" data-testid="point-wait-msg">
              {pointStatus === "sending" ? "Enviando monto a la terminal física…"
                : (pointMsg || (pointOrderStatus === "at_terminal" ? "La terminal recibió el cobro. Procese la tarjeta." : "Orden creada. Esperando que la terminal la descargue."))}
            </p>
            {pointTech?.order_id && <p className="text-xs text-white/40 mt-2 font-mono" data-testid="point-order-id">Orden: {pointTech.order_id}</p>}
          </div>
          <div className="font-display text-4xl font-black text-[#FFCC00]">${coTicket ? granTotalTarjeta.toFixed(2) : "0.00"}</div>
          {pointStatus === "waiting" && (
            <div className="flex gap-2 w-full">
              <button onClick={consultarManual} data-testid="point-consultar-btn"
                className="btn-hard flex-1 h-11 bg-white/5 text-white border border-white/20 font-bold uppercase tracking-wider text-xs flex items-center justify-center gap-1"><RefreshCw className="h-3.5 w-3.5" /> Consultar estado</button>
              <button onClick={cancelarPendiente} data-testid="point-cancelar-btn"
                className="btn-hard flex-1 h-11 bg-white/5 text-[#F87171] border border-[#F87171]/40 font-bold uppercase tracking-wider text-xs">Cancelar cobro</button>
            </div>
          )}
          {pointTech && <PointTech tech={pointTech} />}
        </div>
      )}

      {pointStatus === "timeout" && (
        <div className="py-6 flex flex-col items-center text-center gap-4" data-testid="point-timeout">
          <Clock className="h-14 w-14 text-[#FFCC00]" strokeWidth={1.5} />
          <p className="font-display text-lg font-black tracking-tight text-[#FFCC00]">Terminal no ha recibido la orden</p>
          <p className="text-sm text-white/60">La orden fue creada, pero la terminal aún no la ha recibido. Revise la conexión de la terminal y presione Actualizar en el dispositivo.</p>
          {pointTech?.order_id && <p className="text-xs text-white/40 font-mono">Orden: {pointTech.order_id}</p>}
          <div className="flex gap-2 w-full">
            <button onClick={consultarManual} data-testid="point-actualizar-btn"
              className="btn-hard flex-1 h-12 bg-[#009EE3] text-white border border-[#009EE3] font-bold uppercase tracking-wider text-sm flex items-center justify-center gap-1"><RefreshCw className="h-4 w-4" /> Actualizar</button>
            <button onClick={cancelarPendiente} data-testid="point-cancelar-timeout-btn"
              className="btn-hard flex-1 h-12 bg-white/5 text-[#F87171] border border-[#F87171]/40 font-bold uppercase tracking-wider text-sm">Cancelar</button>
          </div>
          {pointTech && <PointTech tech={pointTech} />}
        </div>
      )}

      {pointStatus === "error" && (
        <div className="py-8 flex flex-col items-center text-center gap-4" data-testid="point-error">
          <XCircle className="h-16 w-16 text-[#F87171]" strokeWidth={1.5} />
          <p className="font-display text-xl font-black tracking-tight text-[#F87171]">El cobro no se completó</p>
          <p className="text-sm text-white/60" data-testid="point-error-msg">{pointMsg || "Rechazado o cancelado en la terminal. Puedes reintentar."}</p>
          <div className="flex gap-2 w-full">
            <button onClick={() => { setPointStatus("idle"); setPointTxId(null); setPointTech(null); setPointOrderStatus(null); setPointMsg(""); }} data-testid="point-retry-btn"
              className="btn-hard flex-1 h-12 bg-[#009EE3] text-white border border-[#009EE3] font-bold uppercase tracking-wider text-sm">Reintentar</button>
            <button onClick={cerrarPagoPoint} data-testid="point-cerrar-btn"
              className="btn-hard flex-1 h-12 bg-white/5 text-white border border-white/20 font-bold uppercase tracking-wider text-sm">Cerrar</button>
          </div>
          {pointTech && <PointTech tech={pointTech} />}
        </div>
      )}
    </DialogContent>
  </Dialog>
);

export default PointDialog;
